import React, { useState, useEffect, useMemo } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import atlassianChampionsLogo from '../../assets/images/champions/atlassian-champions-logo.png';
import Button from '@atlaskit/button';
import { submitChampionSignup } from '../../services/http/ChampionGraphQlOperations';
import {
  FormErrors,
  FormValues,
  initialValues,
  SUCCESS_CHAMPION_PREFERENCES,
} from '../../types/ChampionApplicationFormTypes';
import { validateChampionApplication } from '../../utils/ChampionApplicationFormValidation';
import { sendUIEvent, sendTrackEvent } from '../../services/analytics';
import {
  getFormValidationFailurePayload,
  getFormSubmissionPayload,
  getCommonChampionsAttributes,
  getFormAbandonmentPayload,
  getFormFieldProgressPayload,
  calculateFormCompletion,
} from '../../analytics/champions-helpers';
import { ANALYTICS_ACTIONS } from '../../analytics/constants';
import { toISOBirthDate } from '../../utils/birthDateUtils';
import Logger from 'services/sentry/sentry';

import { PersonalInfoSection } from './sections/PersonalInfoSection';
import { AddressSection } from 'components/champions/sections/AddressSection';
import { WorkPartnerSection } from 'components/champions/sections/WorkPartnerSection';
import { ProductsSection } from 'components/champions/sections/ProductsSection';
import { MotivationSection } from 'components/champions/sections/MotivationSection';
import { PreferenceSection } from 'components/champions/sections/PreferenceSection';
import { PreferenceDetailsSection } from 'components/champions/sections/PreferenceDetailsSection';
import { HowHeardSection } from 'components/champions/sections/HowHeardSection';
import { AgreementsSection } from 'components/champions/sections/AgreementsSection';

/**
 * Champions Application Form
 * 
 * Comprehensive form for Champions program applications with multi-step sections
 * and integrated analytics tracking for form interactions and submissions.
 * 
 * Analytics Strategy:
 * - Tracks form submission attempts and outcomes (success/failure/validation errors)
 * - Measures time from page load to successful submission
 * - Logs validation failures with specific field names for UX improvement
 * - Captures error details for troubleshooting
 * - Includes user journey context (entry point, user ID, authentication state)
 */
const FORM_TYPE = 'championsApplicationForm';

/** Prohibition icon (circle with diagonal slash) for error toast */
const ProhibitionIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="19.07" y1="4.93" x2="4.93" y2="19.07" />
  </svg>
);

type ChampionApplicationFormProps = {
  aaid: string;
  accountIsManaged: boolean;
  emailId: string;
  profileId: string;
  userName: string;
  homeBaseUrl: string;
  userId?: string;
  entryPoint?: string;
};

const ChampionApplicationForm: React.FC<ChampionApplicationFormProps> = ({
  aaid,
  accountIsManaged,
  emailId,
  profileId,
  userName,
  homeBaseUrl,
  userId,
  entryPoint = 'directAccess',
}) => {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    | { type: 'success'; message: string }
    | { type: 'error'; specificError?: string }
    | null
  >(null);

  const [showValidationToast, setShowValidationToast] = useState(false);

  const formStartTime = useMemo(() => Date.now(), []);
  
  // Get common analytics attributes for all events
  const baseAttributes = getCommonChampionsAttributes(
    entryPoint,
    !!userId,
    userId,
    true, // Feature gate is always enabled when this form is shown
  );

  // Track form abandonment when user leaves without submitting
  useEffect(() => {
    /**
     * Handles form abandonment when user leaves the form without completing submission.
     * 
     * Tracks multiple abandonment scenarios:
     * - Back button click (onpopstate event)
     * - Tab close (beforeunload/pagehide events)
     * 
     * Only tracks if user has filled at least one field AND hasn't successfully submitted.
     * Checks submitStatus to prevent tracking abandonment after successful form submission.
     */
    const trackAbandonmentEvent = (reason: string) => {
      // Only track abandonment if form has been interacted with
      // (has some filled fields) AND form was not successfully submitted
      const completion = calculateFormCompletion(values);
      const hasSuccessfullySubmitted = submitStatus?.type === 'success';
      
      if (completion.filledFieldCount > 0 && !hasSuccessfullySubmitted) {
        const abandonmentPayload = getFormAbandonmentPayload(
          FORM_TYPE,
          values,
          baseAttributes,
          formStartTime,
          [],
          reason,
        );

        // Send abandonment event
        sendTrackEvent(
          abandonmentPayload.source,
          abandonmentPayload.actionSubject,
          abandonmentPayload.action,
          abandonmentPayload.attributes,
          userId,
        );
      }
    };

    // Handle back button navigation
    const handlePopState = () => {
      trackAbandonmentEvent('backButton');
    };

    // Handle page/tab close and navigation away
    const handleBeforeUnload = () => {
      trackAbandonmentEvent('pageUnload');
    };

    // Attach listeners for different abandonment scenarios
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [values, submitStatus, baseAttributes, userId]);

  useEffect(() => {
    if (!showValidationToast) return;
    const timer = setTimeout(() => setShowValidationToast(false), 5000);
    return () => clearTimeout(timer);
  }, [showValidationToast]);

  useEffect(() => {
    setValues((prev) => ({ ...prev, aaid }));
  }, [aaid]);

  const PREFERENCE_DEPENDENT_KEYS: (keyof FormValues)[] = [
    'howContributeOnline',
    'attendedAce',
    'aceExperience',
    'fourEventsPerYear',
    'activityAndEvent',
    'whyToCreateContent',
    'contentSharingFrequency',
    'bestContent1',
    'bestContent2',
    'bestContent3',
    'primarySocialProfile',
    'otherURL',
    'commitSharingContent',
    'trainingFrequency',
    'trainingSources',
    'activeCug',
    'eventsPerYear',
    'discussInCoE',
    'coeFocus',
    'otherExcellence',
  ];

  const HOW_HEARD_DEPENDENT_KEYS: (keyof FormValues)[] = [
    'howHeardOther',
    'howHeardEmployee',
    'referrerName',
  ];

  const updateField = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    if (key === 'championPreference') {
      const nextPreference = value as FormValues['championPreference'];
      const prevPreference = values.championPreference;
      const wasSuccessChampion =
        prevPreference !== null && SUCCESS_CHAMPION_PREFERENCES.includes(prevPreference);
      const isSuccessChampion =
        nextPreference !== null && SUCCESS_CHAMPION_PREFERENCES.includes(nextPreference);
      /** Only when moving between Success Champion tracks — keep 4th agreement + its error */
      const keepSuccessChampionAgreementState = wasSuccessChampion && isSuccessChampion;

      setValues((prev) => ({
        ...prev,
        championPreference: nextPreference,
        consentSuccessChampionAgreement: keepSuccessChampionAgreementState
          ? prev.consentSuccessChampionAgreement
          : initialValues.consentSuccessChampionAgreement,
        ...Object.fromEntries(
          PREFERENCE_DEPENDENT_KEYS.map((k) => [k, initialValues[k]])
        ),
      }));
      setErrors((prev) => {
        const next = { ...prev };
        PREFERENCE_DEPENDENT_KEYS.forEach((k) => delete next[k]);
        if (prev.championPreference) delete next.championPreference;
        if (!keepSuccessChampionAgreementState) {
          delete next.consentSuccessChampionAgreement;
        }
        return next;
      });
    } else if (key === 'howHeard') {
      setValues((prev) => ({
        ...prev,
        howHeard: value as FormValues['howHeard'],
        ...Object.fromEntries(
          HOW_HEARD_DEPENDENT_KEYS.map((k) => [k, initialValues[k]])
        ),
      }));
      setErrors((prev) => {
        const next = { ...prev };
        HOW_HEARD_DEPENDENT_KEYS.forEach((k) => delete next[k]);
        if (prev.howHeard) delete next.howHeard;
        return next;
      });
    } else if (key === 'attendedAce') {
      setValues((prev) => ({
        ...prev,
        attendedAce: value as FormValues['attendedAce'],
        aceExperience: initialValues.aceExperience,
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.aceExperience;
        if (prev.attendedAce) delete next.attendedAce;
        return next;
      });
    } else if (key === 'coeFocus') {
      setValues((prev) => ({
        ...prev,
        coeFocus: value as FormValues['coeFocus'],
        otherExcellence: initialValues.otherExcellence,
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.otherExcellence;
        if (prev.coeFocus) delete next.coeFocus;
        return next;
      });
    } else {
      setValues((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const updateMultiSelect = (key: keyof FormValues, optionId: string) => {
    setValues((prev) => {
      const current = prev[key] as string[];
      const exists = current.includes(optionId);
      const next = exists ? current.filter((id) => id !== optionId) : [...current, optionId];
      const updates: Partial<FormValues> = { [key]: next as any };
      if (key === 'products' && optionId === 'other' && exists) {
        updates.otherAtlassianProducts = initialValues.otherAtlassianProducts;
      }
      return { ...prev, ...updates };
    });
    setErrors((prev) => {
      const next = { ...prev };
      if (prev[key]) delete next[key];
      if (key === 'products' && optionId === 'other') {
        delete next.otherAtlassianProducts;
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // UI Event: Submit button clicked
    sendUIEvent(
      FORM_TYPE,
      'button',
      ANALYTICS_ACTIONS.CLICKED,
      'championsApplicationSubmit',
      {
        ...baseAttributes,
        formType: FORM_TYPE,
      },
      userId,
    );

    const validationErrors = validateChampionApplication(values, { accountIsManaged });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Track validation failure with helper
      const validationPayload = getFormValidationFailurePayload(
        FORM_TYPE,
        Object.keys(validationErrors),
        baseAttributes,
        Date.now() - formStartTime,
      );
      sendTrackEvent(
        validationPayload.source,
        validationPayload.actionSubject,
        validationPayload.action,
        validationPayload.attributes,
        userId,
      );
      Logger.warn(`[ChampionApplicationForm] Validation failed aaid=${values.aaid}`);
      setShowValidationToast(true);
      return;
    }

    Logger.info(`[ChampionApplicationForm] Submitting form aaid=${values.aaid}`);

    setIsSubmitting(true);
    setSubmitStatus(null);

    // Track form field progress on submission
    const progressPayload = getFormFieldProgressPayload(
      FORM_TYPE,
      values,
      baseAttributes,
      formStartTime,
    );
    sendTrackEvent(
      progressPayload.source,
      progressPayload.actionSubject,
      progressPayload.action,
      progressPayload.attributes,
      userId,
    );

    // Track form submission with timing
    const submittedPayload = getFormSubmissionPayload(
      'submitted',
      FORM_TYPE,
      baseAttributes,
      Date.now() - formStartTime,
    );
    sendTrackEvent(
      submittedPayload.source,
      submittedPayload.actionSubject,
      submittedPayload.action,
      submittedPayload.attributes,
      userId,
    );

    const baseUrl =
      process.env['REACT_APP_ENVI'] === 'local' ? 'http://localhost:9000' : process.env['HOME_BASE_URL'];
    try {
      const valuesToSend = { ...values };
      valuesToSend.birthDate = toISOBirthDate(values.birthDate);
      const result = await submitChampionSignup(valuesToSend as typeof values, baseUrl, baseUrl);
      if (result?.success) {
        // Track successful submission
        const successPayload = getFormSubmissionPayload(
          'succeeded',
          FORM_TYPE,
          baseAttributes,
          Date.now() - formStartTime,
        );
        sendTrackEvent(
          successPayload.source,
          successPayload.actionSubject,
          successPayload.action,
          successPayload.attributes,
          userId,
        );
        Logger.info(`[ChampionApplicationForm] Submission successful aaid=${values.aaid}`);
        setSubmitStatus({
          type: 'success',
          message: 'Thank you! Your application has been received. Please check your email for next steps.',
        });
        setValues({ ...initialValues, aaid });
        setErrors({});
      } else {
        // Track API error
        const failurePayload = getFormSubmissionPayload(
          'failed',
          FORM_TYPE,
          baseAttributes,
          Date.now() - formStartTime,
          result?.message ?? 'Unknown error',
        );
        sendTrackEvent(
          failurePayload.source,
          failurePayload.actionSubject,
          failurePayload.action,
          failurePayload.attributes,
          userId,
        );
        Logger.error(new Error('[ChampionApplicationForm] Submission failed'), {
          extra: {
            aaid: values.aaid,
            message: result?.message,
            result,
          },
        });
        setSubmitStatus({
          type: 'error',
          specificError: result?.message ?? undefined,
        });
      }
    } catch (error) {
      const err =
        error instanceof Error
          ? error
          : new Error('[ChampionApplicationForm] Unexpected error during submission');
      Logger.error(err, {
        extra: {
          aaid: values.aaid,
          ...(error instanceof Error ? {} : { thrown: error }),
        },
      });

      // Track exception
      const exceptionPayload = getFormSubmissionPayload(
        'failed',
        FORM_TYPE,
        baseAttributes,
        Date.now() - formStartTime,
        err.message,
      );
      sendTrackEvent(
        exceptionPayload.source,
        exceptionPayload.actionSubject,
        exceptionPayload.action,
        exceptionPayload.attributes,
        userId,
      );

      setSubmitStatus({
        type: 'error',
        specificError: err.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus?.type === 'success') {
    return (
      <div css={successPageStyles}>
        <div css={successMessageContainerStyles}>
          <p css={successMessageTextStyles}>{submitStatus.message}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showValidationToast && (
        <div css={validationToastStyles} role="alert" aria-live="assertive">
          <div css={validationToastContentStyles}>
            <span css={validationToastIconStyles} role="img" aria-label="Error">
              <ProhibitionIcon />
            </span>
            <div css={validationToastTextStyles}>
              <div css={validationToastTitleStyles}>Error</div>
              <div>Please fill all the required fields</div>
            </div>
          </div>
          <button
            type="button"
            css={validationToastCloseButtonStyles}
            onClick={() => setShowValidationToast(false)}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
      )}
    <div className="champion-form" css={outerStyles}>
      <div css={formColumnStyles}>
        <div css={logoContainerStyles}>
          <div css={logoCenterStyles}>
            <img
              src={atlassianChampionsLogo}
              alt="Atlassian Champions"
              css={logoStyles}
            />
          </div>
        </div>
        <div css={innerStyles}>
          <form css={formStyles} onSubmit={handleSubmit}>
            <h1 css={headingLargeStyles}>Apply to be an Atlassian Champion</h1>

          <p css={[introTextStyles, marginBottomXSmallStyles]}>
            Questions marked with an asterisk<abbr css={introAsteriskStyles} title="required">*</abbr> are required.
          </p>
          <p css={[introTextStyles, marginBottomMediumStyles]}>
            Issues with this form? We&rsquo;re here to help. Email us at communitymanagers@atlassian.com
          </p>

          <h2 css={headingMediumStyles}>Before you begin</h2>
          <p
            css={[
              introTextStyles,
              accountIsManaged ? marginBottom63pxStyles : marginBottomBeforeBannerStyles,
            ]}
          >
            We&rsquo;re excited for you to start your champion journey. You&rsquo;ll need to meet some requirements to
            join the program. See{' '}
            <a
              href="https://community.atlassian.com/champions#whos-eligible-to-apply"
              target="_blank"
              rel="noopener noreferrer"
            >
              Who&rsquo;s eligible to apply?
            </a>
          </p>

          {!accountIsManaged && (
          <div css={bannerContainerStyles}>
            <div css={bannerIconWrapperStyles}>
              <svg
                viewBox="0 0 16 16"
                width="14"
                height="14"
                fill="none"
                aria-hidden="true"
                css={bannerIconStyles}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0Z"
                  fill="#357DE8"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.5 6.75V8.25H7.25V12.5H8.75V7.5C8.75 7.08579 8.41421 6.75 8 6.75H6.5ZM8 3.5C7.44772 3.5 7 3.94772 7 4.5C7 5.05228 7.44772 5.5 8 5.5C8.55228 5.5 9 5.05228 9 4.5C9 3.94772 8.55228 3.5 8 3.5Z"
                  fill="#FFFFFF"
                />
              </svg>
            </div>
            <div css={bannerTextBoxStyles}>
              Applying to be a Success Champion? Make sure you&rsquo;re logged in with an Atlassian account that uses an
              organization email.
            </div>
          </div>
          )}

          <PersonalInfoSection
            values={values}
            updateField={updateField}
            emailId={emailId}
            profileId={profileId}
            userName={userName}
            homeBaseUrl={homeBaseUrl}
            errors={{
              firstName: errors.firstName,
              lastName: errors.lastName,
              email: errors.email,
              communityProfileUrl: errors.communityProfileUrl,
              linkedinUrl: errors.linkedinUrl,
            }}
          />

          <AddressSection
            values={values}
            updateField={updateField}
            errors={{
              street: errors.street,
              city: errors.city,
              stateProvince: errors.stateProvince,
              postalCode: errors.postalCode,
              country: errors.country,
              birthDate: errors.birthDate,
            }}
          />

          <WorkPartnerSection values={values} updateField={updateField} errors={{ partnerType: errors.partnerType }} />

          <ProductsSection
            values={values}
            updateMultiSelect={updateMultiSelect}
            updateField={updateField}
            errors={{ products: errors.products, otherAtlassianProducts: errors.otherAtlassianProducts }}
          />

          <MotivationSection
            values={values}
            updateField={updateField}
            errors={{ whyBecomeChampion: errors.whyBecomeChampion }}
          />

          <PreferenceSection
            values={values}
            updateField={updateField}
            accountIsManaged={accountIsManaged}
            errors={{ championPreference: errors.championPreference }}
          />

          <PreferenceDetailsSection
            values={values}
            updateField={updateField}
            errors={{
              howContributeOnline: errors.howContributeOnline,
              attendedAce: errors.attendedAce,
              aceExperience: errors.aceExperience,
              fourEventsPerYear: errors.fourEventsPerYear,
              activityAndEvent: errors.activityAndEvent,
              whyToCreateContent: errors.whyToCreateContent,
              contentSharingFrequency: errors.contentSharingFrequency,
              bestContent1: errors.bestContent1,
              bestContent2: errors.bestContent2,
              bestContent3: errors.bestContent3,
              primarySocialProfile: errors.primarySocialProfile,
              otherURL: errors.otherURL,
              commitSharingContent: errors.commitSharingContent,
              trainingFrequency: errors.trainingFrequency,
              trainingSources: errors.trainingSources,
              activeCug: errors.activeCug,
              eventsPerYear: errors.eventsPerYear,
              discussInCoE: errors.discussInCoE,
              coeFocus: errors.coeFocus,
              otherExcellence: errors.otherExcellence,
            }}
          />

          <HowHeardSection
            values={values}
            updateField={updateField}
            errors={{
              howHeard: errors.howHeard,
              howHeardOther: errors.howHeardOther,
              howHeardEmployee: errors.howHeardEmployee,
              referrerName: errors.referrerName,
            }}
          />

          <AgreementsSection
            homeBaseUrl={homeBaseUrl}
            values={values}
            updateField={updateField}
            errors={{
              consentProgramAgreement: errors.consentProgramAgreement,
              consentPrivacyPolicy: errors.consentPrivacyPolicy,
              consentNda: errors.consentNda,
              consentSuccessChampionAgreement: errors.consentSuccessChampionAgreement,
            }}
          />

          {submitStatus?.type === 'error' && (
            <div css={errorBannerStyles}>
              <div>Oops! Something went wrong.</div>
              {submitStatus.specificError && (
                <div css={errorBannerDetailStyles}>{submitStatus.specificError}</div>
              )}
            </div>
          )}

          <div css={marginTopLargeStyles} className="champion-form-submit">
            <Button
              type="submit"
              appearance="primary"
              isDisabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting\u2026' : 'Submit'}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default ChampionApplicationForm;

const successPageStyles = css({
  display: 'flex',
  width: '100%',
  minHeight: '400px',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: token('space.600'),
});

const successMessageContainerStyles = css({
  maxWidth: '600px',
});

const successMessageTextStyles = css({
  color: token('color.text'),
  fontFamily: 'sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  lineHeight: '24px',
  textAlign: 'center',
});

const outerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 0,
  overflow: 'visible',
  padding: token('space.600'),
  paddingTop: token('space.075'), /* minimal distance from nav bar */
  /* SLDS-matching compact text inputs: 2rem height, 0.8125rem font */
  '& [data-ds--text-field--container]': {
    height: '2rem',
    minHeight: '2rem',
    maxHeight: '2rem',
    padding: 0,
    borderRadius: '0.25rem',
    borderColor: '#c9c9c9',
    backgroundColor: '#fff',
  },
  '& [data-ds--text-field--container] input': {
    fontSize: '0.8125rem',
    padding: '0 0.75rem',
    height: '100%',
  },
});

const formColumnStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

const logoContainerStyles = css({
  width: '100%',
  maxWidth: '686px', /* 10% larger than 624px */
  marginBottom: '12px', /* slightly more space between logo and heading */
});

const innerStyles = css({
  width: '100%',
  maxWidth: '600px',
});

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.0'),
});

const logoCenterStyles = css({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
});

const logoStyles = css({
  maxWidth: '686px', /* 10% larger than 624px */
  width: '100%',
  height: 'auto',
});

const headingLargeStyles = css({
  fontSize: '22.4pt', /* 30% smaller than 32pt */
  fontWeight: 600,
  fontFamily: 'sans-serif',
  marginBottom: token('space.300'),
});

const headingMediumStyles = css({
  fontSize: '18.2pt', /* 30% smaller than 26pt */
  fontWeight: 600,
  fontFamily: 'sans-serif',
  marginTop: token('space.100'),
  marginBottom: token('space.200'),
});

const introTextStyles = css({
  fontSize: '10pt',
  fontFamily: 'sans-serif',
});

const marginBottomXSmallStyles = css({
  marginBottom: token('space.100'),
});

const marginBottomMediumStyles = css({
  marginBottom: token('space.200'),
});

const marginBottomBeforeBannerStyles = css({
  marginBottom: '24px',
});

const marginBottom63pxStyles = css({
  marginBottom: '63px',
});

const bannerContainerStyles = css({
  width: '100%',
  maxWidth: '600px',
  minHeight: '56px',
  backgroundColor: '#E9F2FE',
  marginBottom: '63px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'baseline',
  paddingTop: '16px',
  paddingLeft: '8px',
  paddingRight: '8px',
  paddingBottom: '16px',
  boxSizing: 'border-box',
});

const bannerIconWrapperStyles = css({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '8px',
  marginTop: '4px',
  lineHeight: 0,
});

const bannerIconStyles = css({
  height: '14px',
  width: '14px',
  display: 'block',
});

const bannerTextBoxStyles = css({
  flex: 1,
  minHeight: '24px',
  minWidth: 0,
  backgroundColor: 'transparent',
  color: '#000000',
  fontWeight: 400,
  fontSize: '10pt',
  fontFamily: 'sans-serif',
  lineHeight: '24px',
  display: 'flex',
  alignItems: 'flex-start',
});

const marginTopLargeStyles = css({
  marginTop: token('space.200'),
});

/* Intro asterisk: same color as text, not red */
const introAsteriskStyles = css({
  color: 'inherit',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const errorBannerStyles = css({
  padding: token('space.200'),
  borderRadius: '0.25rem',
  backgroundColor: '#fef1ee',
  color: 'var(--champion-form-error)',
  fontWeight: 400,
});

const errorBannerDetailStyles = css({
  marginTop: token('space.050'),
  fontSize: '0.875rem',
});

/* Validation toast – centered below navbar (68px), matches SLDS slds-theme--error slds-notify--toast */
const validationToastStyles = css({
  position: 'fixed',
  top: '68px', /* below fixed navbar (matches scroll-padding-top in global.css) */
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10000,
  display: 'flex',
  alignItems: 'flex-start',
  gap: token('space.150'),
  padding: '0.75rem 1rem', /* SLDS slds-notify padding */
  backgroundColor: '#ea001e', /* --slds-g-color-error-base-40 */
  color: '#fff', /* --slds-g-color-on-error-1 */
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.16)', /* SLDS notify shadow */
  borderRadius: '0.25rem',
});

const validationToastContentStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.150'),
  flex: 1,
  minWidth: 0,
});

const validationToastCloseButtonStyles = css({
  flexShrink: 0,
  padding: 0,
  margin: 0,
  marginLeft: token('space.050'),
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '0.25rem',
  ':hover': { opacity: 0.85 },
  ':focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
});

const validationToastIconStyles = css({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
});

const validationToastTextStyles = css({
  flex: 1,
  minWidth: 0,
});

const validationToastTitleStyles = css({
  fontWeight: 600,
  fontSize: '0.875rem',
  marginBottom: token('space.025'),
});
